// @ts-check
import React, { useRef, useState, useCallback, useEffect } from "react";
import { DayPicker } from "react-day-picker";
import Button from "react-bootstrap/lib/Button";
import "react-day-picker/dist/style.css";
import * as Locales from "date-fns/locale";
import dayjs from "dayjs";
import styles from "./DayPickerInput.module.css";
import loc from "i18next";
import { Reference, Popper } from "react-popper";
import { createPortal } from "react-dom";
import FocusTrap from "focus-trap-react";
import ClearableInput from "@hs/clearable-input";
import LocalDate from "containers/LocalDate";

export const getDateFnsLocale = () => {
    let [language, country] = dayjs.locale().split("-");
    language = language?.toLowerCase();
    country = country?.toUpperCase();
    switch (`${language}${country == null ? "" : "-" + country}`) {
        case "en":
            return Locales["enUS"];
        case "de-CH":
            return Locales["de"];
        default:
            return Locales[`${language}${country == null ? "" : country}`];
    }
};

const DayPickerInput = ({
    inputProps,
    placeholder,
    format: dateFormat,
    value: valueFromProps,
    onDayChange,
    component: Input = "input",
    overlayComponent: Overlay = "div",
    required = false,
    autoFocus = false,
    dayPickerProps,
    id,
}) => {
    /* istanbul ignore next */
    const [selected, setSelected] = useState(
        valueFromProps ? dayjs(valueFromProps, dateFormat, true).toDate() : ""
    );

    const [value, setValue] = useState(valueFromProps);

    // month to display
    const [month, setMonth] = useState(
        selected || dayPickerProps?.month || new Date()
    );

    useEffect(() => {
        setValue(valueFromProps);
    }, [valueFromProps]);

    useEffect(() => {
        /* istanbul ignore next */
        setSelected((s) =>
            value && dayjs(value, dateFormat).isValid(value, dateFormat)
                ? dayjs(value, dateFormat, true).toDate()
                : s
        );
    }, [value, dateFormat, setSelected]);

    const [isPopperOpen, setIsPopperOpen] = useState(false);
    const [isFocusTrapActive, setIsFocusTrapActive] = useState(false);

    const focus = useRef({ input: autoFocus, overlay: false });

    const popperRef = useRef(null);

    const closePopper = useCallback(() => {
        /* istanbul ignore next */
        if (focus.current.input || focus.current.overlay) return;

        setIsPopperOpen(false);
    }, [focus]);

    const handleInputChange = useCallback(
        (e) => {
            setValue(e);
            const dateObj = dayjs(e, dateFormat);
            if (dateObj.isValid(e, dateFormat)) {
                const date = dateObj.toDate();
                onDayChange(date, popperRef.current);
                setSelected(date);
                // change the month to switch the daypicker view
                setMonth(date);
            } else {
                if ((e ?? "").trim() === "") {
                    onDayChange(null, popperRef.current, null, true);
                    setSelected(undefined);
                } else {
                    onDayChange(null, popperRef.current, e);
                }
            }

            // if the input was cleared by the X button then
            // we have to blur and focus again so the FocusTrap becomes active
            // otherwise the date is not selectable and the popup just closes on click
            // bugticket for proper fix: Bug 66350: @hs/clearable-input breaks DayPickerInput

            /* istanbul ignore next */
            if (e == null) {
                setTimeout(() => {
                    popperRef.current._ref.blur();
                    popperRef.current._ref.focus();
                });
            }
        },
        [dateFormat, onDayChange, setSelected]
    );

    const handleFocus = useCallback((e) => {
        focus.current.input = true;
        setIsPopperOpen(true);
    }, []);

    const handleBlur = useCallback(() => {
        focus.current.input = false;
        closePopper();
    }, [closePopper]);

    const handleDaySelect = useCallback(
        (date: Date, prevDate: Date) => {
            // reset the time to 00:00:00 to ensure proper comparison
            date?.setHours(0, 0, 0, 0);
            // set the selected date and close the popper
            setSelected((prevSelected) => {
                let selected = date;
                if (date || +prevDate === +prevSelected) {
                    // we do NOT want to reset the date on selecting it again
                    /* istanbul ignore next */
                    selected = date || prevSelected;
                    const formattedDate = dayjs(selected).format(
                        dayjs.localeData().longDateFormat("L")
                    );
                    setValue(formattedDate);
                    onDayChange(selected, popperRef.current, formattedDate);
                    focus.current.overlay = false;
                    focus.current.input = false;
                    setIsFocusTrapActive(false);
                    closePopper();
                } else {
                    onDayChange(null, popperRef.current, null, true);
                    setValue("");
                }
                return selected;
            });
        },
        [closePopper, onDayChange, setSelected]
    );

    /* istanbul ignore next */
    const handleActivate = useCallback(() => {
        focus.current.overlay = true;
    }, []);

    const handleDeactivate = useCallback(() => {
        focus.current.overlay = false;
        setIsFocusTrapActive(false);
        closePopper();
    }, [closePopper]);

    const setToday = useCallback(() => {
        handleDaySelect(new Date());
        setMonth(new Date());
    }, [handleDaySelect]);

    return (
        <>
            <Reference>
                {({ ref }) => (
                    <div ref={ref} className="day-picker-input-wrapper">
                        <ClearableInput
                            // do not rework the ref to use a function, it will break functionality (see: Bug 71850: Filter for date columns cannot select any dates)
                            ref={popperRef}
                            placeholder={placeholder}
                            value={value}
                            onChange={handleInputChange}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            {...inputProps}
                            required={required}
                            autoFocus={autoFocus}
                            className="form-control"
                            id={id}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleDeactivate();
                                }
                            }}
                        />
                    </div>
                )}
            </Reference>
            {isPopperOpen &&
                createPortal(
                    <Popper
                        modifiers={[
                            {
                                name: "preventOverflow",
                                enabled: true,
                                options: {
                                    rootBoundary: "window",
                                },
                            },
                        ]}
                        positionFixed={false}
                        placement="bottom-end"
                    >
                        {({ ref, style, placement }) => (
                            <FocusTrap
                                active={isFocusTrapActive}
                                focusTrapOptions={{
                                    initialFocus: false,
                                    allowOutsideClick: true,
                                    clickOutsideDeactivates: true,
                                    returnFocusOnDeactivate: false,
                                    onActivate: handleActivate,
                                    onDeactivate: handleDeactivate,
                                }}
                            >
                                <div
                                    ref={ref}
                                    style={{
                                        ...style,
                                        backgroundColor: "#f5f5f5",
                                    }}
                                    className={styles.overlay}
                                    data-placement={placement}
                                    onMouseDown={() =>
                                        setIsFocusTrapActive(true)
                                    }
                                >
                                    <DayPicker
                                        mode="single"
                                        defaultMonth={selected}
                                        selected={selected}
                                        onSelect={handleDaySelect}
                                        locale={getDateFnsLocale()}
                                        onMonthChange={setMonth}
                                        modifiersStyles={{
                                            selected: {
                                                backgroundColor: "#0098aa",
                                            },
                                        }}
                                        {...dayPickerProps}
                                        month={month}
                                    />
                                    <Button
                                        bsStyle="link"
                                        className={styles.todayButton}
                                        onClick={setToday}
                                        data-test="dayPickerTodayButton"
                                    >
                                        {loc.t(
                                            "folder:action_filter.calendar_today"
                                        )}
                                    </Button>
                                </div>
                            </FocusTrap>
                        )}
                    </Popper>,
                    document.getElementById("root")
                )}
        </>
    );
};

export default LocalDate(DayPickerInput);
